import { useEffect, useState } from "react";
import { Label, makeStyles, tokens } from "@fluentui/react-components";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IFileSizeInTenantReportSummaryProps } from "./file-size-in-tenant-report-summary.props";
import PercentageBar from "components/common/percentage-bar/percentage-bar";
import CheckboxIndicator from "components/common/displays/checkbox-indicator";
import { getSizeLabel } from "lib/utils";
import { FileSizeUnit, FilterOperator } from "lib/enums";

const layoutStyles = makeStyles({
    mainWrapper: {
        display: 'flex',
        flexDirection: 'row',
        gap: '48px'
    },
    criteriaSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    criteriaRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '8px'
    },
    operatorLabel: {
        marginLeft: '28px',
        color: tokens.colorNeutralForeground3
    },
    statsSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    statsRow: {
        display: 'flex',
        flexDirection: 'column'
    },
    statsLabel: {
        fontWeight: tokens.fontWeightSemibold
    }
});

export default function FileSizeInTenantReportSummary(props: IFileSizeInTenantReportSummaryProps) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();

    const [quotaLabel, setQuotaLabel] = useState<string>("");

    useEffect(() => {
        if (props.report?.details) {
            const used = getSizeLabel(props.report.details.totalFileSizeKB, 'KB');
            const quota = getSizeLabel(props.report.details.storageQuotaMB, 'MB');
            setQuotaLabel(`(${t("ppaQuotaUsedValue", { used: used, quota: quota })})`);
        }
    }, [props.report?.details]);

    // Get unit label
    const getUnitLabel = (unit: number): string => {
        return unit === FileSizeUnit.GB ? "GB" : "MB";
    };

    // Get operator label
    const getOperatorLabel = (operator: number): string => {
        return operator === FilterOperator.Or ? t("ppaOr") : t("ppaAnd");
    };

    return (
        <div className={styles.mainWrapper}>
            {props.report?.details && (
                <>
                    {/* Filter criteria display */}
                    <div className={styles.criteriaSection}>
                        <CheckboxIndicator
                            checked={props.report.details.filterByVersionCount}
                            label={`${t("ppaMoreThan")} ${props.report.details.minVersionCount ?? 0} ${t("ppaVersions")}`}
                        />

                        {props.report.details.filterByVersionCount && props.report.details.filterByFileSize && (
                            <Label className={styles.operatorLabel}>
                                {getOperatorLabel(props.report.details.filterOperator)}
                            </Label>
                        )}

                        <CheckboxIndicator
                            checked={props.report.details.filterByFileSize}
                            label={`${t("ppaSizeLargerThan")} ${props.report.details.minFileSize ?? 0} ${getUnitLabel(props.report.details.fileSizeUnit)}`}
                        />
                    </div>

                    {/* Statistics */}
                    <div className={styles.statsSection}>
                        <div className={styles.statsRow}>
                            <Label className={styles.statsLabel}>{t("ppaFileCount")}</Label>
                            <span>{props.report.details.fileCount.toLocaleString()}</span>
                        </div>

                        <div className={styles.statsRow}>
                            <Label className={styles.statsLabel}>{t("ppaTotalFileSizes")}</Label>
                            <span>{getSizeLabel(props.report.details.totalFileSizeKB, 'KB')}</span>
                        </div>

                        <div className={styles.statsRow}>
                            <Label className={styles.statsLabel}>{t("ppaPercentageOfQuota")}</Label>
                            <PercentageBar
                                value={props.report.details.percentageOfQuota}
                                valueDecimalPlaces={2}
                                showLabel={true}
                                additionalLabel={quotaLabel}
                            />
                        </div>

                        <div className={styles.statsRow}>
                            <Label className={styles.statsLabel}>{t("ppaWorkspaceCount")}</Label>
                            <span>{props.report.details.workspaceCount.toLocaleString()}</span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
